<template>
  <section :class="['feature-section py-5', backgroundColor]">
    <div class="container">
      <div class="row align-items-center" :class="{ 'flex-row-reverse': imageRight }">
        <div class="col-md-6">
          <h2 class="section-title mb-4" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">{{ title }}</h2>
          <p class="section-subtitle mb-4" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">{{ subtitle }}</p>
          <div class="features-list" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
            <div v-for="(feature, index) in features" :key="index" class="feature-item mb-3">
              <i :class="[feature.icon, 'feature-icon']" :style="{ marginRight: $i18n.locale === 'ar' ? '0' : '1rem', marginLeft: $i18n.locale === 'ar' ? '1rem' : '0' }"></i>
              <div class="feature-content">
                <h4 class="feature-title">{{ feature.title }}</h4>
                <p class="feature-description">{{ feature.description }}</p>
              </div>
            </div>
          </div>
          <div class="flex flex-wrap gap-4 mt-8" :class="{ 'justify-end': imageRight }">
            <a
              href="https://wa.me/966561730527"
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center px-8 py-3 rounded-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1 bg-[#faf6df] text-[#0f4640] hover:bg-opacity-90"
            >
              {{ $i18n.locale === 'ar' ? 'تعرف على المزيد' : 'Learn More' }}
            </a>
          </div>
          <div class="testimonial mt-5" v-if="testimonial" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
            <p class="testimonial-text">"{{ testimonial.text }}"</p>
            <div class="testimonial-author">
              — {{ testimonial.author }}, {{ testimonial.position }}
            </div>
          </div>
        </div>
        <div class="col-md-6">
          <div class="feature-image">
            <img :src="image" :alt="imageAlt" class="img-fluid rounded shadow">
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: 'FeatureSection',
  props: {
    title: {
      type: String,
      required: true
    },
    subtitle: {
      type: String,
      required: true
    },
    features: {
      type: Array,
      required: true,
      validator: function(value) {
        return value.every(feature => 
          typeof feature === 'object' && 
          'icon' in feature && 
          'title' in feature && 
          'description' in feature
        )
      }
    },
    ctas: {
      type: Array,
      required: true,
      validator: function(value) {
        return value.every(cta => 
          typeof cta === 'object' && 
          'text' in cta && 
          'primary' in cta
        )
      }
    },
    image: {
      type: String,
      required: true
    },
    imageAlt: {
      type: String,
      required: true
    },
    imageRight: {
      type: Boolean,
      default: false
    },
    backgroundColor: {
      type: String,
      default: 'bg-white'
    },
    testimonial: {
      type: Object,
      validator: function(value) {
        return value === null || (
          typeof value === 'object' &&
          'text' in value &&
          'author' in value &&
          'position' in value
        )
      }
    }
  },
  mounted() {
    this.observeElements();
  },
  methods: {
    observeElements() {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      }, {
        threshold: 0.1
      });

      document.querySelectorAll('.feature-content, .testimonial-card').forEach(el => {
        observer.observe(el);
      });
    }
  }
}
</script>

<style scoped>
:root {
  /* Core colors */
  --primary-color: #0f4640;
  --primary-light: #1f9285;
  --secondary-color: #f7f6e1;
  --accent-color: #f1f5f9;
  
  /* Text colors */
  --text-primary: #0f4640;
  --text-secondary: #1f9285;
  --text-body: #334155;
  --text-light: #f7f6e1;
  
  /* Background colors */
  --bg-primary: #f1f5f9;
  --bg-secondary: #f7f6e1;
  --bg-white: #ffffff;
  
  /* Gradients */
  --gradient-primary: linear-gradient(135deg, #0f4640 0%, #1f9285 100%);
  --gradient-light: linear-gradient(135deg, #f1f5f9 0%, #f7f6e1 100%);
  
  /* Effects */
  --box-shadow: 0 10px 30px rgba(15, 70, 64, 0.1);
  --box-shadow-hover: 0 20px 40px rgba(15, 70, 64, 0.15);
  --transition-base: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  --font-primary: "Cairo", system-ui, -apple-system, sans-serif;
}

.feature-section {
  position: relative;
  overflow: hidden;
  padding: 6rem 0;
  background-color: var(--bg-primary);
  color: var(--text-primary);
}

.feature-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230f4640' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.1;
  z-index: 0;
}

.section-title {
  font-family: var(--font-primary);
  font-size: clamp(2.25rem, 5vw, 3.5rem);
  font-weight: 800;
  margin-bottom: 1.75rem;
  line-height: 1.2;
  letter-spacing: -0.02em;
  position: relative;
  color: var(--text-primary);
  text-shadow: none;
}

.section-subtitle {
  font-family: var(--font-primary);
  font-size: clamp(1.2rem, 3vw, 1.4rem);
  margin-bottom: 3.5rem;
  line-height: 1.6;
  font-weight: 400;
  color: var(--text-body);
}

.feature-item {
  display: flex;
  align-items: flex-start;
  padding: 1.75rem;
  margin-bottom: 1.5rem;
  border-radius: 1rem;
  background: var(--bg-white);
  border: 1px solid rgba(15, 70, 64, 0.1);
  transition: var(--transition-base);
  position: relative;
  overflow: hidden;
  box-shadow: var(--box-shadow);
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: var(--box-shadow-hover);
  border-color: var(--primary-color);
  background: var(--bg-white);
}

.feature-item::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 3px;
  background: var(--gradient-primary);
  transform: scaleX(0);
  transition: var(--transition-base);
}

.feature-item:hover::before {
  transform: scaleX(1);
}

.feature-icon {
  width: 3.5rem;
  height: 3.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--bg-secondary);
  color: var(--primary-color);
  border-radius: 1rem;
  font-size: 1.5rem;
  transition: var(--transition-base);
  position: relative;
  overflow: hidden;
  margin-right: 1.5rem;
}

[dir="rtl"] .feature-icon {
  margin-right: 0;
  margin-left: 1.5rem;
}

.feature-item:hover .feature-icon {
  background: var(--primary-color);
  color: var(--text-light);
  transform: rotate(10deg);
}

.feature-content {
  flex: 1;
}

.feature-title {
  font-family: var(--font-primary);
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 0.75rem;
  color: var(--text-primary);
  transition: var(--transition-base);
  line-height: 1.4;
}

.feature-description {
  font-size: 1rem;
  line-height: 1.7;
  color: var(--text-body);
  margin: 0;
}

.feature-image {
  position: relative;
  border-radius: 1.5rem;
  overflow: hidden;
  box-shadow: var(--box-shadow);
  transition: var(--transition-base);
  background: var(--bg-white);
  padding: 0.5rem;
}

.feature-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: var(--transition-base);
  border-radius: 1rem;
}

.feature-image:hover {
  transform: translateY(-10px);
  box-shadow: var(--box-shadow-hover);
}

.feature-image:hover img {
  transform: scale(1.05);
}

.testimonial {
  background: var(--bg-white);
  padding: 2rem;
  border-radius: 1rem;
  margin-top: 3rem;
  position: relative;
  border: 1px solid rgba(15, 70, 64, 0.1);
  box-shadow: var(--box-shadow);
  transition: var(--transition-base);
}

.testimonial:hover {
  transform: translateY(-5px);
  box-shadow: var(--box-shadow-hover);
  border-color: var(--primary-color);
}

.testimonial::before {
  content: '"';
  position: absolute;
  top: -1rem;
  left: 2rem;
  font-size: 5rem;
  color: var(--primary-color);
  opacity: 0.1;
  font-family: serif;
  line-height: 1;
}

.testimonial-text {
  font-size: 1.1rem;
  line-height: 1.8;
  color: var(--text-primary);
  margin-bottom: 1.5rem;
  font-style: italic;
  position: relative;
  padding: 0 1rem;
}

.testimonial-text::before,
.testimonial-text::after {
  content: '"';
  position: absolute;
  font-size: 2rem;
  color: var(--primary-color);
  opacity: 0.2;
  font-family: serif;
  line-height: 1;
}

.testimonial-text::before {
  top: -0.5rem;
  left: -0.5rem;
}

.testimonial-text::after {
  bottom: -1.5rem;
  right: -0.5rem;
}

.testimonial-author {
  font-weight: 600;
  color: var(--text-secondary);
  font-size: 1rem;
  margin-top: 1rem;
}

/* WhatsApp button style */
a[href^="https://wa.me"] {
  background: var(--bg-secondary) !important;
  color: var(--primary-color) !important;
  font-weight: 700;
  transition: var(--transition-base);
  box-shadow: var(--box-shadow);
}

a[href^="https://wa.me"]:hover {
  transform: translateY(-3px);
  box-shadow: var(--box-shadow-hover);
  background: var(--bg-white) !important;
}

@media (max-width: 768px) {
  .feature-section {
    padding: 4rem 0;
  }

  .feature-item {
    padding: 1.5rem;
  }

  .feature-icon {
    width: 3rem;
    height: 3rem;
    font-size: 1.25rem;
    margin-right: 1rem;
  }

  [dir="rtl"] .feature-icon {
    margin-right: 0;
    margin-left: 1rem;
  }

  .feature-title {
    font-size: 1.15rem;
    margin-bottom: 0.5rem;
  }

  .feature-description {
    font-size: 0.95rem;
    line-height: 1.6;
  }

  .testimonial {
    padding: 1.5rem;
    margin-top: 2rem;
  }

  .testimonial-text {
    font-size: 1rem;
  }
}

/* RTL specific styles */
[dir="rtl"] {
  text-align: right;
}

[dir="rtl"] .testimonial-text::before {
  left: auto;
  right: -0.5rem;
}

[dir="rtl"] .testimonial-text::after {
  right: auto;
  left: -0.5rem;
}

/* Animation classes */
.visible {
  animation: fadeInUp 0.6s ease-out forwards;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style> 